import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Building2, CircleUser } from 'lucide-react';

import {
  getCompanyQueryKey,
  loadQuery as queryCompanies,
} from 'common/actions/thirdPartyCompanyQueries';
import { getUserQueryKey, loadQuery as queryUsers } from 'common/actions/userQueries';
import UserAvatarV2 from 'common/common/UserAvatarV2';
import connect from 'common/core/connect';
import HStack from 'common/ui/HStack';
import { Select, type SelectBaseOption } from 'common/uiV3/Select';
import useDelayer from 'common/util/useDelayer';

import type { User } from 'common/api/endpoints/users';
import type { Option } from 'common/ui/common/select/SelectCommon';
import type { State } from 'redux-connect';

type UserSelectProps = {
  isLoading: boolean;
  onChange: (value: string | undefined) => void;
  onSearchChange: (value: string | undefined) => Promise<SelectBaseOption[]>;
  options: SelectBaseOption[];
  selectedUserId: string | undefined;
};

const UserSelect = ({
  options,
  onChange,
  onSearchChange,
  selectedUserId,
  isLoading,
}: UserSelectProps) => (
  <Select
    options={options}
    placeholder={
      <>
        <CircleUser /> User
      </>
    }
    value={selectedUserId}
    onValueChange={onChange}
    onSearchChange={onSearchChange}
    isSearchable
    fullWidth
    isLoading={isLoading}
    allowClear
  />
);

type CompanySelectProps = {
  isLoading: boolean;
  onChange: (value: string | undefined) => void;
  onSearchChange: (value: string | undefined) => Promise<SelectBaseOption[]>;
  options: SelectBaseOption[];
  selectedCompanyId: string | undefined;
};

const CompanySelect = ({
  isLoading,
  options,
  onChange,
  onSearchChange,
  selectedCompanyId,
}: CompanySelectProps) => (
  <Select
    options={options}
    placeholder={
      <>
        <Building2 /> Company
      </>
    }
    isLoading={isLoading}
    value={selectedCompanyId}
    onValueChange={onChange}
    onSearchChange={onSearchChange}
    isSearchable
    fullWidth
    allowClear
  />
);

export type OnUserCompanyChange = ({
  thirdPartyCompanyID,
  userID,
}: {
  thirdPartyCompanyID: string | undefined;
  userID: string | undefined;
}) => void;

type OwnProps = {
  defaultUser?: Option;
  defaultCompany?: Option;
  onChange: OnUserCompanyChange;
};

type ConnectStateProps = {
  thirdPartyCompanies: any;
  thirdPartyCompanyQueries: any;
  users: any;
  userQueries: any;
};

type ConnectDispatchProps = {
  searchCompanies: (queryParams: { search: string; userID?: string }) => void;
  searchUsers: (queryParams: { search: string; thirdPartyCompanyID?: string }) => void;
};

type UserCompanySelectProps = OwnProps & ConnectStateProps & ConnectDispatchProps;

const UserCompanySelect = (props: UserCompanySelectProps & OwnProps) => {
  const {
    defaultUser,
    defaultCompany,
    onChange,
    searchCompanies,
    searchUsers,
    thirdPartyCompanies,
    thirdPartyCompanyQueries,
    users,
    userQueries,
  } = props;

  const [userSearchValue, setUserSearchValue] = useState('');
  const [companySearchValue, setCompanySearchValue] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(defaultUser?.value);

  const [userLoading, setUserLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(
    defaultCompany?.value,
  );

  const [autoAssignCompanyFlag, setAutoAssignCompanyFlag] = useState(false);

  const performUserSearch = useCallback(
    async (value: string, thirdPartyCompanyID?: string) => {
      setUserLoading(true);
      await searchUsers({ search: value, thirdPartyCompanyID });
      setUserLoading(false);
    },
    [searchUsers, setUserLoading],
  );

  const performCompanySearch = useCallback(
    async (value: string, userID?: string) => {
      setCompanyLoading(true);
      await searchCompanies({ search: value, userID });
      setCompanyLoading(false);
    },
    [searchCompanies, setCompanyLoading],
  );

  const onUserSearchChange = useCallback(
    async (value) => {
      setUserSearchValue(value);
      performUserSearch(value, selectedCompanyId);
      return [];
    },
    [performUserSearch, selectedCompanyId],
  );

  const onCompanySearchChange = useCallback(
    async (value) => {
      if (selectedCompanyId) {
        return;
      }

      setCompanySearchValue(value);
      performCompanySearch(value, selectedUserId);
    },
    [performCompanySearch, selectedCompanyId, selectedUserId],
  );

  const onUserChange = useCallback(
    async (newUserId: string | undefined) => {
      setSelectedUserId(newUserId);
      setUserSearchValue('');
      onChange({ thirdPartyCompanyID: selectedCompanyId, userID: newUserId });

      if (newUserId && !selectedCompanyId) {
        performCompanySearch('', newUserId);
      }
      setAutoAssignCompanyFlag(true);
    },
    [onChange, performCompanySearch, selectedCompanyId],
  );

  const onCompanyChange = useCallback(
    async (newCompanyId: string | undefined) => {
      setSelectedCompanyId(newCompanyId);
      setCompanySearchValue('');
      onChange({ thirdPartyCompanyID: newCompanyId, userID: selectedUserId });

      if (newCompanyId && !selectedUserId) {
        performUserSearch('', newCompanyId);
      }
    },
    [onChange, performUserSearch, selectedUserId],
  );

  useEffect(() => {
    // Load initial users and companies on mount
    performUserSearch('', undefined);
    performCompanySearch('', undefined);
  }, [performCompanySearch, performUserSearch]);

  const userQueryKey = getUserQueryKey({
    search: userSearchValue,
    thirdPartyCompanyID: selectedCompanyId,
  });
  const userQueryResult = userQueries[userQueryKey];
  const userOptions = useMemo<SelectBaseOption[]>(
    () =>
      userQueryResult?.userIDs?.map((userID: string) => {
        const user = users[userID];
        return {
          label: (
            <HStack className="items-center gap-2">
              <UserAvatarV2 className="shrink-0" user={user} size={16} />
              <div className="truncate">{user.name}</div>
            </HStack>
          ),
          value: user._id,
        };
      }) || [],
    [userQueryResult, users],
  );

  const companyQueryKey = getCompanyQueryKey({
    search: companySearchValue,
    userID: selectedUserId,
  });
  const companyQueryResult = thirdPartyCompanyQueries[companyQueryKey];
  const companyOptions = useMemo<SelectBaseOption[]>(
    () =>
      companyQueryResult?.companyIDs?.map((companyID: string) => {
        const tpc = thirdPartyCompanies[companyID];
        return {
          label: tpc.name,
          value: tpc.thirdPartyCompanyID,
        };
      }) || [],
    [companyQueryResult, thirdPartyCompanies],
  );

  useEffect(() => {
    if (!autoAssignCompanyFlag) {
      return;
    }

    if (companyOptions.length === 1) {
      onCompanyChange(companyOptions[0].value);
    }
    setAutoAssignCompanyFlag(false);
  }, [autoAssignCompanyFlag, companyOptions, onCompanyChange]);

  return (
    <>
      <UserSelect
        isLoading={userLoading}
        onChange={onUserChange}
        onSearchChange={useDelayer(onUserSearchChange, 500)}
        options={userOptions}
        selectedUserId={selectedUserId}
      />
      <CompanySelect
        isLoading={companyLoading}
        onChange={onCompanyChange}
        onSearchChange={useDelayer(onCompanySearchChange, 500)}
        options={companyOptions}
        selectedCompanyId={selectedCompanyId}
      />
    </>
  );
};

const connectDispatchProps: ConnectDispatchProps = {
  searchUsers: queryUsers,
  searchCompanies: queryCompanies,
};

export default connect(
  (state: State): ConnectStateProps => ({
    thirdPartyCompanies: state.thirdPartyCompanies,
    thirdPartyCompanyQueries: state.thirdPartyCompanyQueries,
    users: Object.values(state.users).reduce(
      (acc: Record<string, User>, user) => {
        const typedUser = user as User;
        acc[typedUser._id] = typedUser;
        return acc;
      },
      {} as Record<string, User>,
    ),
    userQueries: state.userQueries,
  }),
  connectDispatchProps,
)(UserCompanySelect) as unknown as React.FC<OwnProps>;
